import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, Books, MapPin, Calendar, LockSimple } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Constants ─────────────────────────────────────────
const COLORS = ["#F59E0B","#38BDF8","#34D399","#FBBF24","#F472B6","#A78BFA","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#6366F1","#14B8A6","#D946EF"];

const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #2563EB, #60A5FA)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function LibraryCommunity() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainData, setMainData] = useState<any[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [branch, setBranch] = useState("all");

  // ── Fetch main data ──
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (branch !== "all") params.set("branch", branch);
      const [mainRes, compRes, trendRes] = await Promise.all([
        accessService.get(`/library/usage?${params}`),
        accessService.get(`/library/comparison?fy=${fy}&metric=total_loans`),
        accessService.get(`/library/trend?metric=total_visits`),
      ]);
      setMainData(mainRes.data);
      setComparisonData(compRes.data);
      setTrendData(trendRes.data);
    } catch (err: any) {
      setError(err.message || "Failed to load library data");
    } finally {
      setLoading(false);
    }
  }, [fy, branch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Dropdown options ──
  const branchOptions: IDropdownOption[] = useMemo(() => {
    const unique = Array.from(new Set(mainData.map((r: any) => r.branch_name).filter(Boolean))).sort();
    return [
      { key: "all", text: "All Branches" },
      ...unique.map(b => ({ key: b, text: b })),
    ];
  }, [mainData]);

  // ── KPIs ──
  const kpis = useMemo(() => {
    if (mainData.length === 0) return null;
    const totalLoans = mainData.reduce((s: number, r: any) => s + (Number(r.total_loans) || 0), 0);
    const totalVisits = mainData.reduce((s: number, r: any) => s + (Number(r.total_visits) || 0), 0);
    const activeMembers = mainData.reduce((s: number, r: any) => s + (Number(r.active_members) || 0), 0);
    const programAttendees = mainData.reduce((s: number, r: any) => s + (Number(r.program_attendees) || 0), 0);
    return { totalLoans, totalVisits, activeMembers, programAttendees };
  }, [mainData]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "Library", color: "#2563EB", label: "Total Loans", value: fmtNum(kpis.totalLoans), sub: `FY ${fy}` },
      { icon: "People", color: "#34D399", label: "Total Visits", value: fmtNum(kpis.totalVisits), sub: `FY ${fy}` },
      { icon: "Contact", color: "#F59E0B", label: "Active Members", value: fmtNum(kpis.activeMembers), sub: "all branches" },
      { icon: "Event", color: "#F472B6", label: "Program Attendees", value: fmtNum(kpis.programAttendees), sub: `FY ${fy}` },
    ];
  }, [kpis, fy]);

  // ── Chart data ──
  const loansByBranch = useMemo(() => {
    return comparisonData
      .map((r: any) => ({ name: r.label || r.branch_name || r.sa2_name, value: Number(r.value || r.total_loans) || 0 }))
      .sort((a: any, b: any) => b.value - a.value);
  }, [comparisonData]);

  const visitsTrend = useMemo(() => {
    return trendData.map((r: any) => ({ financial_year: r.financial_year, value: Number(r.value || r.total_visits) || 0 }));
  }, [trendData]);

  const membershipPie = useMemo(() => {
    const byBranch: Record<string, number> = {};
    mainData.forEach((r: any) => {
      const name = r.branch_name || "Unknown";
      byBranch[name] = (byBranch[name] || 0) + (Number(r.active_members) || 0);
    });
    return Object.entries(byBranch).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  }, [mainData]);

  const programsByBranch = useMemo(() => {
    const byBranch: Record<string, number> = {};
    mainData.forEach((r: any) => {
      const name = r.branch_name || "Unknown";
      byBranch[name] = (byBranch[name] || 0) + (Number(r.program_attendees) || 0);
    });
    return Object.entries(byBranch)
      .map(([name, value]) => ({ name, value }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [mainData]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && mainData.length === 0) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading library & community data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#2563EB', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Library & Community</div>
          <div className={styles.subtitle}>
            Loans, visits, memberships and community programs across Boroondara's library branches.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><Books size={12} weight="regular" /> Library & Community</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> 8 Branches</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> FY 2022-23 to 2024-25</span>
            <span className={styles.chip}><LockSimple size={12} weight="regular" /> OFFICIAL: Sensitive</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Branch</div>
            <Dropdown
              selectedKey={branch}
              options={branchOptions}
              onChange={(_, opt) => opt && setBranch(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Bar: Loans by Branch */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Loans by Branch ({fy})</div>
            {loansByBranch.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={loansByBranch} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Bar dataKey="value" name="Total Loans" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No loans data available
              </div>
            )}
          </div>

          {/* Line: Visits Trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Visits Trend</div>
            {visitsTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={visitsTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Line type="monotone" dataKey="value" name="Total Visits" stroke="#34D399" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartsRow}>
          {/* Pie: Membership Distribution */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Membership Distribution ({fy})</div>
            {membershipPie.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={membershipPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${truncLabel(name, 12)} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {membershipPie.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No membership data available
              </div>
            )}
          </div>

          {/* Bar: Programs by Branch */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Program Attendees by Branch ({fy})</div>
            {programsByBranch.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={programsByBranch} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Bar dataKey="value" name="Attendees" fill="#F472B6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No program data available
              </div>
            )}
          </div>
        </div>

        {/* ── Data Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>Library Data — FY {fy}</div>
          {mainData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Branch</th>
                  <th className={styles.th}>Loans</th>
                  <th className={styles.th}>Visits</th>
                  <th className={styles.th}>Members</th>
                  <th className={styles.th}>Programs</th>
                  <th className={styles.th}>New Members</th>
                  <th className={styles.th}>FY</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row: any, i: number) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.branch_name}</td>
                    <td className={styles.td}>{fmtNum(row.total_loans)}</td>
                    <td className={styles.td}>{fmtNum(row.total_visits)}</td>
                    <td className={styles.td}>{fmtNum(row.active_members)}</td>
                    <td className={styles.td}>{fmtNum(row.program_attendees)}</td>
                    <td className={styles.td}>{fmtNum(row.new_memberships)}</td>
                    <td className={styles.td}>{row.financial_year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No library data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Library & Community Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about library loans, visits, memberships and community programs across Boroondara branches.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
